// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo, useState } from 'react'
import {
  ColumnDef,
  Flex,
  Table,
  Typography,
  Tag,
  SortingState,
  useShowHide,
} from '@teamnine/pcj-ui-v2'
import { Business } from '@/utils/material-icons'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'

import { Customer, getCustomers } from '@/services/customers'
import { getTagColor, getTagLabel } from '@/utils/helpers'
import Page from '@/app/shared/components/Layout/Page'
import CreateCustomerModal from '@/app/CustomerManagement/components/CreateCustomerModal'
import PageHeader from '@/app/shared/components/PageHeader'
import LinkButton from '@/app/shared/components/LinkButton'
import Panel from '@/app/shared/components/Panel'

const PAGE_SIZE = 30

export default function CustomerManagement() {
  const navigate = useNavigate()
  const createCustomerModal = useShowHide()
  const [pageIndex, setPageIndex] = useState(0)
  const [sortState, setSortState] = useState<SortingState>([])
  const [searchString, setSearchString] = useState('')

  const { data, isLoading } = useQuery('getCustomers', getCustomers)

  const customers = useMemo(() => {
    if (!data) return []

    const filtered = searchString
      ? data.filter((customer) => {
          const search = searchString.trim().toLowerCase()
          return (
            customer.name.toLowerCase().includes(search) ||
            customer.accountGcn.toLowerCase().startsWith(search)
          )
        })
      : data

    if (!sortState.length) return filtered

    const { id, desc } = sortState[0]
    return [...filtered].sort((a, b) => {
      let result = 0

      if (id === 'name') {
        result = a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      } else if (id === 'accountGcn') {
        result = a.accountGcn.localeCompare(b.accountGcn, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      } else if (id === 'status') {
        const aStatus = a.status ?? 0
        const bStatus = b.status ?? 0

        result = aStatus > bStatus ? 1 : aStatus < bStatus ? -1 : 0

        if (result === 0) {
          result = a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
      }

      return desc ? -result : result
    })
  }, [data, sortState, searchString])

  const pagedCustomers = useMemo(() => {
    const start = pageIndex * PAGE_SIZE
    return customers.slice(start, start + PAGE_SIZE)
  }, [customers, pageIndex])

  const columns: ColumnDef<Customer>[] = useMemo(() => {
    return [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <LinkButton
            label={row.original.name}
            onClick={() => navigate(`/customer-management/${row.original.id}`)}
          />
        ),
      },
      {
        id: 'accountGcn',
        header: 'GCN',
        accessorKey: 'accountGcn' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.accountGcn || 'N/A'}</Typography>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: true,
        cell: ({ row }) => {
          return (
            <Tag color={getTagColor(row.original)}>
              {getTagLabel(row.original)}
            </Tag>
          )
        },
      },
    ]
  }, [navigate])

  return (
    <Page>
      <Page.Content>
        <Panel useContent={false} p={16}>
          <Flex direction="column" gap={16}>
            <PageHeader
              title="Customer Management"
              action={
                <CreateCustomerModal
                  customers={customers}
                  modal={createCustomerModal}
                />
              }
            />
            <Table<Customer>
              borderless
              data={pagedCustomers}
              isLoading={isLoading}
              columns={columns}
              pagination={{
                mode: 'paged',
                pageIndex,
                pageSize: PAGE_SIZE,
                totalCount: customers.length,
                onPageChange: ({ pageIndex }) => {
                  setPageIndex(pageIndex)
                },
              }}
              sorting={{
                state: sortState,
                onSortingChange: (next) => {
                  setSortState(next)
                  setPageIndex(0)
                },
              }}
              search={{
                type: 'single',
                onSearch: (val: string[]) => {
                  setSearchString(val[0])
                  setPageIndex(0)
                },
                placeholder: 'Enter customer name',
                label: 'Search Customer',
                width: 400,
              }}
              empty={{
                title: 'There are no customers.',
                Icon: Business,
              }}
            />
          </Flex>
        </Panel>
      </Page.Content>
    </Page>
  )
}
